import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components/macro";
import _ from "lodash";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { format, isAfter, isSameDay, differenceInCalendarDays } from "date-fns";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Fade } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { ButtonIcon } from "@loanbossinc/component-library";
import { mapCompanySelector } from "store/state/auth";
import Avatar from "@material-ui/core/Avatar";

import { getReportGroups, getPermissionedUsers, getPermissionedUser } from "./lenderReportingDashboard.store";
import { getAssetManagerInitials } from "./lenderReportingDashboard.helper";
import { TaskStatus, normalizeDate, LrdRoleType, LrdReportFrequency, LrdIconValues, defaultFilterState, BackButtonText } from "./constants";
import HeaderComponent from "./defaultHeaderComponent";
import FilterDrawer from "./filterDrawer/filterDrawer";

const WIDTH = "986px";

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 1120px;
  min-width: 1120px;
  margin: auto;
  align-items: center;
`;

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-top: 30vh;
  width: 100%;
`;

const SearchTextSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1em 0;
  border-bottom: 1px solid #dadce0;
  font-family: Work Sans;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: #5f6368;
  & > div {
    width: ${WIDTH};
  }
`;

const AccordionSummaryDealName = styled.div`
  margin-left: 14px;
`;

const AccordionSummaryDealText = styled.div`
  float: left;
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  margin-right: 14px;
`;

const AccordionSummaryTotalReports = styled.div`
  background: #ffffff;
  border: 0.5px solid #000000;
  border-radius: 8px;
  box-sizing: border-box;
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: #000000;
  float: left;
  width: 85px;
`;

const AccordionSummaryOverduePlaceholder = styled.div`
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  text-align: right;
  text-transform: uppercase;
  color: #c40a0a;
  display: flex;
  align-items: center;
  width: 220px;
  height: 100%;
`;

const StyledAccordionDetails = styled.div`
  color: #5f6368;
  display: flex;
  width: 100%;
  padding: 10px 20px;
  align-items: center;
  cursor: pointer;
`;

const AccordionDetailReportInformation = styled.div`
  width: 60%;
  padding: 0.3em 0;
`;

const AccordionDetailReportName = styled.div`
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 130%;
  color: #5f6368;
  margin-bottom: 0.3em;
  text-transform: uppercase;
`;

const AccordionDetailReportingPeriod = styled.div`
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 130%;
  color: #5f6368;
`;

const AccordionDetailReportDue = styled.div`
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: #5f6368;
  float: right;
  width: 15%;
  display: flex;
  align-items: center;
  border-right: 1px solid #dadce0;
  padding: 0.3em 0;
  height: 100%;
  box-sizing: border-box;
`;

const AccordionDetailReportGroupStatus = styled.div`
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  text-align: right;
  text-transform: uppercase;
  color: #c40a0a;
  width: 25%;
  padding: 0.3em 0;
  color: ${({ textColor }) => textColor};
`;

const ViewingDateSection = styled.div`
  display: flex;
  justify-content: flex-start;
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  width: 986px;
  margin-top: 1em;
`;

const AccordionGroupWrapper = styled.div`
  width: calc(100% - 6em);
  margin: 2em 0;
`;

const AccordionGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
`;

const AccordionRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border-left: 1px solid #dadce0;
  border-right: 1px solid #dadce0;
  border-bottom: 1px solid #dadce0;
  background-color: #ffffff;

  &:first-child {
    border-radius: 8px 8px 0px 0px;
    border-top: 1px solid #dadce0;
  }

  &:last-child {
    border-radius: 0px 0px 8px 8px;
    border-bottom: 1px solid #dadce0;
  }

  &:only-child {
    border-radius: 8px;
  }
`;

export const StyledAvatar = styled(Avatar)`
  && {
    background: #0F9AD6;
    margin-right: auto;
    font-weight: normal;
    font-size: 16px;
  }
`;

const AccordionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1em;
  box-sizing: border-box;
  box-shadow: ${props => (props.isExpanded ? "0px 10px 20px rgba(173, 174, 174, 0.25)" : "none")};
  z-index: 1;
  min-height: 70px;
`;
const AccordionHeaderRowLeftSection = styled.div`
  display: flex;
  align-items: center;
  max-width: 50%;
`;

const AccordionHeaderRowRightSection = styled.div`
  display: flex;
  align-items: center;
`;

const ExpandedRowWrapper = styled.div`
  display: flex;
  border-top: 1px solid #dadce0;
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  min-height: 60px;
`;

const OverdueText = styled.div`
  margin-left: auto;
`;

const ButtonText = styled.div`
  font-family: Work Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
`;

const ButtonWrapper = styled.div`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 19px;
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #005aa9;
    text-decoration: none;
    cursor: pointer;
    padding-top: 4px;
  }
`;

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AccordionExpandable = props => {
  const { headerLeftSectionContent, headerRightSectionContent, rowsContent, isExpanded, onChangeExpansionState } = props;

  return (
    <AccordionRowWrapper>
      <AccordionHeaderRow isExpanded={isExpanded} onClick={onChangeExpansionState}>
        <AccordionHeaderRowLeftSection>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          {headerLeftSectionContent}
        </AccordionHeaderRowLeftSection>
        <AccordionHeaderRowRightSection>{headerRightSectionContent}</AccordionHeaderRowRightSection>
      </AccordionHeaderRow>
      {isExpanded && rowsContent?.length && rowsContent.map((rowContent, index) => <ExpandedRowWrapper key={index}>{rowContent}</ExpandedRowWrapper>)}
    </AccordionRowWrapper>
  );
};

const LenderReportingDashboard = ({
  reportGroups,
  isReportGroupsLoading,
  getReportGroups,
  history,
  authentication,
  getPermissionedUsers,
  permissionedUsers,
  getPermissionedUser
}) => {
  const [initialRgis, setInitialRgis] = useState([]);
  const [filteredRgis, setFilteredRgis] = useState([]);
  const [filteredRgisBySearchAndDate, setFilteredRgisBySearchAndDate] = useState([]);
  const [drawerFilterState, setDrawerFilterState] = useState(defaultFilterState);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showCalendarFilter, setShowCalendarFilter] = useState(false);
  const [isLrdAdmin, setIsLrdAdmin] = useState(false);

  const [expansionState, setExpansionState] = useState({});

  const currentDate = new Date();
  const permissionedUser = permissionedUsers?.[0] || {};
  const debounceDelay = 500;
  const debouncedSearchText = useDebounce(searchText, debounceDelay);

  useEffect(() => {
    getReportGroups();
    getPermissionedUser({ contactId: authentication.user.contact.id });
    getPermissionedUsers({ contactIds: [authentication.user.contact.id] });
  }, [getReportGroups, getPermissionedUser, getPermissionedUsers]);

  useEffect(() => {
    setIsLrdAdmin(permissionedUser?.lenderReportingRoleTypeId === LrdRoleType.ADMIN);
  }, [permissionedUsers]);

  useEffect(() => {
    setIsSystemAdmin(authentication?.isSystemAdmin);
  }, [authentication]);

  // set initial RGIs to show and attach additional properties
  useEffect(() => {
    if (reportGroups && reportGroups.length) {
      const flattenedRgis = reportGroups
        .filter(({ isDeleted }) => !isDeleted)
        .flatMap(({ reportName, reportGroupInstances }) => reportGroupInstances.map(rgi => ({
          ...rgi,
          reportName
        })));

      const actualRgis = flattenedRgis.filter(rgi => new Date(rgi.reportingStartDate) <= currentDate && rgi.isDraft !== true);

      const submittedRgis = [];
      for (const rgi of actualRgis.filter(rgi => (rgi.isSubmitted === true && rgi.isApproved === true) || rgi.isPreviousSubmission === true)) {
        submittedRgis.push({ reportGroupId: rgi.reportGroupId, reportName: rgi.reportName });
      }

      for (const rgi of actualRgis) {
        const hasPreviouslySubmittedReport = submittedRgis.find(x => x.reportGroupId === rgi.reportGroupId && x.reportName === rgi.reportName);
        if (hasPreviouslySubmittedReport) {
          rgi.hasPreviouslySubmittedReport = true;
        }
        if (!rgi.isSubmitted && !isAfter(normalizeDate(rgi?.dueDate), currentDate)) {
          rgi.isOverdue = true;
          rgi.shouldShow = true;
          rgi.status = "overdue";
          rgi.iconName = LrdIconValues.OVERDUE.ICON_NAME;
          rgi.iconText = LrdIconValues.OVERDUE.LABEL;
          rgi.color = LrdIconValues.OVERDUE.COLOR;
        } else if (
          rgi.tasks.filter(x => x.statusId === TaskStatus.COMPLETED).length >= 1 &&
          rgi.tasks.filter(x => x.statusId === TaskStatus.IN_PROGRESS).length >= 1
        ) {
          rgi.shouldShow = true;
          rgi.status = "inProgress";
          rgi.iconText = LrdIconValues.IN_PROGRESS.LABEL;
          rgi.color = LrdIconValues.IN_PROGRESS.COLOR;
        } else if (rgi.tasks.length !== 0 && rgi.tasks.every(x => x.statusId === TaskStatus.COMPLETED) && !rgi.isApproved && !rgi.isSubmitted) {
          rgi.shouldShow = true;
          rgi.status = "readyForApproval";
          rgi.iconName = LrdIconValues.READY_FOR_APPROVAL.ICON_NAME;
          rgi.iconText = LrdIconValues.READY_FOR_APPROVAL.LABEL;
          rgi.color = LrdIconValues.READY_FOR_APPROVAL.COLOR;
        } else if (rgi.isApproved && !rgi.isSubmitted) {
          rgi.shouldShow = true;
          rgi.status = "readyForSubmission";
          rgi.iconName = LrdIconValues.READY_FOR_SUBMISSION.ICON_NAME;
          rgi.iconText = LrdIconValues.READY_FOR_SUBMISSION.LABEL;
          rgi.color = LrdIconValues.READY_FOR_SUBMISSION.COLOR;
        } else if (rgi.isApproved && rgi.isSubmitted) {
          rgi.shouldShow = false;
        } else {
          rgi.shouldShow = true;
          rgi.status = "notStarted";
          rgi.iconName = LrdIconValues.NOT_STARTED.ICON_NAME;
          rgi.iconText = LrdIconValues.NOT_STARTED.LABEL;
          rgi.color = LrdIconValues.NOT_STARTED.COLOR;
        }
      }

      const allShouldShowRgis = _.orderBy(
        actualRgis.filter(rgi => rgi.shouldShow),
        ["dueDate"],
        ["asc"]
      );

      formatRgiText(allShouldShowRgis);
      setInitialRgis(allShouldShowRgis);
    }
  }, [reportGroups]);

  // set initial filtered RGIs
  useEffect(() => {
    if (initialRgis && initialRgis.length) {
      setFilteredRgis(initialRgis);

      // set initial expansion state
      const groupedRgis = _.groupBy(initialRgis, "dealName");
      const deals = Object.keys(groupedRgis);
      const initialExpansionState = {};
      for (const deal of deals) {
        initialExpansionState[deal] = false;
      }
      setExpansionState(initialExpansionState);
    }
  }, [initialRgis]);

  // set filteredRGIsBySearchAndDate based on search text and selected date
  useEffect(() => {
    let filteredByTextAndDate = filteredRgis;
    if (debouncedSearchText.length) {
      filteredByTextAndDate = filteredRgis.filter(rgi => rgi.dealName.toLowerCase().includes(debouncedSearchText.toLowerCase()));
    }

    if (selectedDate) {
      filteredByTextAndDate = filteredByTextAndDate.filter(rgi => isSameDay(normalizeDate(rgi.dueDate), normalizeDate(selectedDate)));
    }

    setFilteredRgisBySearchAndDate(filteredByTextAndDate);
  }, [filteredRgis, debouncedSearchText, selectedDate, setFilteredRgisBySearchAndDate]);

  // apply drawer filter state to filtered RGIs
  useEffect(() => {
    if (initialRgis && initialRgis.length) {
      const { taskAssigneeId, reportAssigneeId, frequency, entity, status } = drawerFilterState;

      const filteredRgisByDrawer = initialRgis.filter(rgi => {
        // BY FREQUENCY
        if (!frequency[rgi.reportingFrequencyId]) {
          return false;
        }

        // BY ENTITY
        if (rgi.deliverableType && !entity[rgi.deliverableType]) {
          return false;
        }

        // BY STATUS
        if (!status[rgi.status]) {
          return false;
        }

        // BY TASK ASSIGNEE
        if (taskAssigneeId !== -1) {
          let taskAssigneeFound = false;
          for (const task of rgi.tasks) {
            const taskAssignees = task.taskAssignees || [];
            const containsNeededAssignee = taskAssignees.some(ta => ta.contactId === taskAssigneeId);
            if (containsNeededAssignee) {
              taskAssigneeFound = true;
            }
          }
          if (!taskAssigneeFound) {
            return false;
          }
        }

        // BY REPORT ASSIGNEE
        if (reportAssigneeId !== -1) {
          let reportAssigneeFound = false;
          if (rgi.assetManager && rgi.assetManagerId === reportAssigneeId) {
            reportAssigneeFound = true;
          }
          if (!reportAssigneeFound) {
            return false;
          }
        }

        return true;
      });

      setFilteredRgis(filteredRgisByDrawer);
    }
  }, [drawerFilterState]);

  const getFormattedDealsListWithRgis = useMemo(() => {
    const groupedDeals = _.groupBy(filteredRgisBySearchAndDate, "dealName");

    // change expansion state to false if filtered out
    const deals = Object.keys(groupedDeals);
    for (const expansionKey in expansionState) {
      if (!deals.includes(expansionKey)) {
        expansionState[expansionKey] = false;
      }
    }

    return Object.entries(groupedDeals)
      .map((x) => {
        const dealName = x[0];
        const allRgis = x[1];

        const overDueRgis = [];
        const notOverDueRgis = [];

        for (const rgi of allRgis) {
          rgi.isOverdue ? overDueRgis.push(rgi) : notOverDueRgis.push(rgi);
        }

        const groupedByReportGroup = _.groupBy(notOverDueRgis, "reportGroupId");

        const currentMonthlyRgis = [];
        const currentBiMonthlyRgis = [];
        const currentQuarterlyRgis = [];
        const currentSemiAnnualRgis = [];
        const currentAnnualRgis = [];

        _.forOwn(groupedByReportGroup, (groupedRgis) => {
          const currentMonthlyRgi = groupedRgis.find((rgi) => rgi.reportingFrequencyId === LrdReportFrequency.MONTHLY);
          const currentBiMonthlyRgi = groupedRgis.find((rgi) => rgi.reportingFrequencyId === LrdReportFrequency.BI_MONTHLY);
          const currentQuarterlyRgi = groupedRgis.find((rgi) => rgi.reportingFrequencyId === LrdReportFrequency.QUARTERLY);
          const currentSemiAnnualRgi = groupedRgis.find((rgi) => rgi.reportingFrequencyId === LrdReportFrequency.SEMI_ANNUALLY);
          const currentAnnualRgi = groupedRgis.find((rgi) => rgi.reportingFrequencyId === LrdReportFrequency.ANNUALLY);

          if (currentMonthlyRgi) {
            currentMonthlyRgis.push(currentMonthlyRgi);
          }

          if (currentBiMonthlyRgi) {
            currentBiMonthlyRgis.push(currentBiMonthlyRgi);
          }

          if (currentQuarterlyRgi) {
            currentQuarterlyRgis.push(currentQuarterlyRgi);
          }

          if (currentSemiAnnualRgi) {
            currentSemiAnnualRgis.push(currentSemiAnnualRgi);
          }

          if (currentAnnualRgi) {
            currentAnnualRgis.push(currentAnnualRgi);
          }
        });

        return {
          dealName,
          assetManager: allRgis.find((y) => y.assetManager !== null) ? allRgis.find((y) => y.assetManager !== null).assetManager : null,
          overDueReportGroupInstances: overDueRgis,
          currentMonthlyRgis,
          currentBiMonthlyRgis,
          currentQuarterlyRgis,
          currentSemiAnnualRgis,
          currentAnnualRgis
        };
      })
      .sort((a, b) => a.dealName.localeCompare(b.dealName));
  }, [filteredRgisBySearchAndDate]);

  const getListOfAssignees = useCallback(() => {
    const taskAssigneeArray = [{ id: -1, name: "Everyone" }];
    const initialRgisWithTasks = initialRgis.filter(rgi => rgi?.tasks?.length > 0);

    initialRgisWithTasks.map(rgi => {
      rgi.tasks.map(task => {
        if (task.taskAssignees) {
          task.taskAssignees.map(taskAssignee => {
            if (taskAssignee.contact && !taskAssigneeArray.some(element => element.id === taskAssignee.contact.id)) {
              taskAssigneeArray.push({
                id: taskAssignee.contact.id,
                name: `${taskAssignee.contact.firstName || ""} ${taskAssignee.contact.lastName || ""}`
              });
            }
          });
        }
      });
    });

    return taskAssigneeArray;
  }, [initialRgis]);

  const getListOfReportAssignees = useCallback(() => {
    const reportAssigneeArray = [{ id: -1, name: "Everyone" }];

    initialRgis.map(rgi => {
      if (rgi.assetManager && !reportAssigneeArray.some(element => element.id === rgi.assetManagerId)) {
        reportAssigneeArray.push({
          id: rgi.assetManagerId,
          name: rgi.assetManager || ""
        });
      }
    });

    return reportAssigneeArray;
  }, [initialRgis]);

  const formatRgiText = reportGroupInstances => {
    for (const rgi of reportGroupInstances) {
      if (rgi.reportName) {
        const splitReportName = rgi.reportName.split(" - ");
        const reportFrequency = splitReportName[2];

        rgi.shortenedReportName = reportFrequency.trim();
      }

      rgi.reportFrequencyText = formatRgiFrequencyText(rgi);
      rgi.daysTillDueText = formatRgiDueDateText(rgi);
    }
  };

  const formatRgiFrequencyText = reportGroupInstance => {
    const asOfDate = new Date(reportGroupInstance.asOfDate);

    if (reportGroupInstance.reportingFrequencyId === LrdReportFrequency.MONTHLY ||
      reportGroupInstance.reportingFrequencyId === LrdReportFrequency.BI_MONTHLY) {
      return format(asOfDate, "MMMM yyyy");
    } else if (reportGroupInstance.reportingFrequencyId === LrdReportFrequency.QUARTERLY) {
      const currentQuarter = Math.floor(asOfDate.getMonth() / 3 + 1);

      return `Q${currentQuarter} ${asOfDate.getFullYear()}`;
    } else if (
      reportGroupInstance.reportingFrequencyId === LrdReportFrequency.SEMI_ANNUALLY ||
      reportGroupInstance.reportingFrequencyId === LrdReportFrequency.ANNUALLY
    ) {
      return `${asOfDate.getFullYear()}`;
    } else {
      return "";
    }
  };

  const formatRgiDueDateText = reportGroupInstance => {
    const normalizedDueDate = normalizeDate(reportGroupInstance.dueDate);
    const normalizedCurrentDate = normalizeDate(currentDate);

    const daysTillReportDue = Math.abs(differenceInCalendarDays(normalizedDueDate, normalizedCurrentDate));

    if (reportGroupInstance.isOverdue) {
      return `Due ${daysTillReportDue} days ago`;
    }

    return `Due in ${daysTillReportDue} days`;
  };

  const goToDetails = reportGroupInstanceId => {
    history.push(`/lenderReportingDashboard/tasks/${reportGroupInstanceId}`, { from: BackButtonText.DASHBOARD.FROM });
  };

  const getTotalReportCount = reportGroup => {
    const overdueReportCount = reportGroup?.overDueReportGroupInstances?.length || 0;
    const currentMonthlyCount = reportGroup?.currentMonthlyRgis?.length || 0;
    const currentBiMonthlyCount = reportGroup?.currentBiMonthlyRgis?.length || 0;
    const currentQuarterlyCount = reportGroup?.currentQuarterlyRgis?.length || 0;
    const currentSemiAnnualCount = reportGroup?.currentSemiAnnualRgis?.length || 0;
    const currentAnnualCount = reportGroup?.currentAnnualRgis?.length || 0;

    return overdueReportCount + currentMonthlyCount + currentBiMonthlyCount + currentQuarterlyCount + currentSemiAnnualCount + currentAnnualCount;
  };

  const toggleCalendarFilter = () => {
    if (showCalendarFilter) {
      setSelectedDate(null);
      setShowCalendarFilter(false);
    } else {
      setShowCalendarFilter(true);
    }
  };

  const determineViewingDate = () => {
    if (isSameDay(normalizeDate(selectedDate), normalizeDate(currentDate))) {
      return "today";
    }

    return format(normalizeDate(selectedDate), "MMMM d, yyyy");
  };

  const renderCollapsibleReportGroupRows = rg => {
    const rows = [];
    if (rg.overDueReportGroupInstances?.length) {
      rows.push(...rg.overDueReportGroupInstances.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }
    if (rg.currentMonthlyRgis?.length) {
      rows.push(...rg.currentMonthlyRgis.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }
    if (rg.currentBiMonthlyRgis?.length) {
      rows.push(...rg.currentBiMonthlyRgis.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }
    if (rg.currentQuarterlyRgis?.length) {
      rows.push(...rg.currentQuarterlyRgis.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }
    if (rg.currentSemiAnnualRgis?.length) {
      rows.push(...rg.currentSemiAnnualRgis.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }
    if (rg.currentAnnualRgis?.length) {
      rows.push(...rg.currentAnnualRgis.map(rgi => renderCollapsibleRgiDetails(rgi)));
    }

    return rows;
  };

  const handleSubmittedReportsClick = (event, rgiId) => {
    event.stopPropagation();
    history.push(`/lenderReportingDashboard/submitted/${rgiId}`, { from: "direct-from-dashboard" });
  };

  const onChangeExpansionState = (dealName, newExpansionState) => {
    setExpansionState({ ...expansionState, [dealName]: newExpansionState });
  };

  const renderCollapsibleRgiDetails = rgi => (
    <StyledAccordionDetails key={rgi?.id} onClick={() => goToDetails(rgi?.id)}>
      <AccordionDetailReportInformation>
        <AccordionDetailReportName>{rgi?.shortenedReportName}</AccordionDetailReportName>
        <AccordionDetailReportingPeriod>{rgi?.reportFrequencyText}</AccordionDetailReportingPeriod>
          {rgi.hasPreviouslySubmittedReport &&
          <>
            <ButtonWrapper>
              <ButtonText onClick={(e) => handleSubmittedReportsClick(e, rgi.id)}>
                View submitted reports
              </ButtonText>
              <div className="material-icons" style={{ fontSize: "18px" }}>
                launch
              </div>
            </ButtonWrapper>
          </>}
      </AccordionDetailReportInformation>
      <AccordionDetailReportDue>{rgi?.daysTillDueText}</AccordionDetailReportDue>
      <AccordionDetailReportGroupStatus textColor={rgi?.color}>
        {rgi?.iconText}
        <ButtonIcon iconName={rgi?.iconName} style={{ color: rgi?.color, marginLeft: "5px" }} />
      </AccordionDetailReportGroupStatus>
    </StyledAccordionDetails>
  );

  const renderReportGroups = formattedRgs => formattedRgs?.map(
    (reportGroup, idx) => getTotalReportCount(reportGroup) !== 0 && (
      <AccordionExpandable
        key={idx}
        dealName={reportGroup.dealName}
        setExpansionState={setExpansionState}
        isExpanded={expansionState[reportGroup.dealName]}
        onChangeExpansionState={() => onChangeExpansionState(reportGroup.dealName, !expansionState[reportGroup.dealName])}
        headerLeftSectionContent={
          <AccordionSummaryDealName>
            <AccordionSummaryDealText>{reportGroup?.dealName}</AccordionSummaryDealText>
            <AccordionSummaryTotalReports>
              {getTotalReportCount(reportGroup)}
              {getTotalReportCount(reportGroup) === 1 ? " report" : " reports"}
            </AccordionSummaryTotalReports>
          </AccordionSummaryDealName>
        }
        headerRightSectionContent={
              reportGroup?.overDueReportGroupInstances?.length !== 0 ? (
                <AccordionSummaryOverduePlaceholder>
                  {reportGroup?.assetManager && (
                    <StyledAvatar>
                      {getAssetManagerInitials(reportGroup?.assetManager)}
                    </StyledAvatar>
                  )}
                  <OverdueText>
                    {reportGroup?.overDueReportGroupInstances?.length}
                    {reportGroup?.overDueReportGroupInstances?.length === 1 ? " report overdue" : " reports overdue"}
                  </OverdueText>
                  <ButtonIcon
                    iconName={LrdIconValues.OVERDUE.ICON_NAME}
                    style={{
                      color: LrdIconValues.OVERDUE.COLOR,
                      marginLeft: "5px"
                    }}
                  />
                </AccordionSummaryOverduePlaceholder>
              ) : (
                reportGroup?.assetManager && <Avatar style={{ backgroundColor: "#0F9AD6", fontSize: "16px" }}>{getAssetManagerInitials(reportGroup?.assetManager)}</Avatar>
        )
        }
        rowsContent={renderCollapsibleReportGroupRows(reportGroup)}
      />
    )
  );

  if (isReportGroupsLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <CircularProgress size="5%" thickness={2} />
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderComponent
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        reportGroupInstances={filteredRgis}
        searchText={searchText}
        setSearchText={value => setSearchText(value)}
        isReportGroupsLoading={isReportGroupsLoading}
        isAdmin={isSystemAdmin || isLrdAdmin}
        toggleDrawer={() => setShowFilterDrawer(!showFilterDrawer)}
        toggleCalendarFilter={toggleCalendarFilter}
        showCalendarFilter={showCalendarFilter}
      />
      <FilterDrawer
        closeDrawer={() => setShowFilterDrawer(!showFilterDrawer)}
        isOpen={showFilterDrawer}
        taskAssignees={getListOfAssignees()}
        reportAssignees={getListOfReportAssignees()}
        setDrawerFilterState={setDrawerFilterState}
        drawerFilterState={drawerFilterState}
      />
      {selectedDate && <ViewingDateSection>{`Viewing ${determineViewingDate()}`}</ViewingDateSection>}
      {searchText && (
        <SearchTextSection>
          <div>
            Showing reports for deals matching '
            {searchText}
            '
          </div>
        </SearchTextSection>
      )}
      <Fade in timeout={1000}>
        <AccordionGroupWrapper>
          <AccordionGroup>{renderReportGroups(getFormattedDealsListWithRgis)}</AccordionGroup>
        </AccordionGroupWrapper>
      </Fade>
    </PageContainer>
  );
};

const mapStateToProps = ({ lenderReportingDashboard, authentication }) => ({
  ...lenderReportingDashboard,
  company: mapCompanySelector(authentication),
  authentication
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getPermissionedUser,
    getReportGroups,
    getPermissionedUsers
  })
)(LenderReportingDashboard);
